import React, { Component } from 'react';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { Group, GroupUpdateReference } from '~/Api/User/ProductGroup/CzReimbursement';
import { formatNumericHumanDate, parseNumericHumanDate } from '~/DateTime';
import { Debounce } from '~/Debounce';
import DatePickerInput from '~/UI/DatePickerInput/DatePickerInput';
import Icon from '~/UI/Icon/Icon';
import defaultInputStyles from '~/UI/Input/compact.less';
import SaveActivityIndicator, { Status } from '~/UI/SaveActivityIndicator/SaveActivityIndicator';
import Select from '~/UI/Select/Select';
import Tooltip from '~/UI/Tooltip/Tooltip';

import { SavingsGroupsAutocomplete } from './SavingsGroupsAutocomplete';
import styles from './styles.less';

interface Props {
	czReimbursement: Group | null;
	group: UserProductGroup;
	versions: ReferenceBasketVersion[] | null;
	updateStatus: Status | null;
	onLoad(groupId: string): void;
	onUnload(groupId: string): void;
	onChange(groupId: string, reference: GroupUpdateReference): void;
}

interface DateValue {
	value: string;
	dateValue: Date | null;
	isValid: boolean | null;
}

type SettingsState = {
	historicProductDataViewDate: DateValue;
	referenceBasketVersion: string;
	possibleSavingsRelativeTo: string[];
};

interface State {
	state: SettingsState | null;
}

export default class Settings extends Component<Props, State> {
	state: State = {
		state: null,
	};

	componentDidMount() {
		const { group, onLoad } = this.props;
		onLoad(group.id);
	}

	componentDidUpdate(prevProps: Readonly<Props>) {
		const { group, onLoad } = this.props;

		if (prevProps.group.id !== group.id) {
			onLoad(group.id);
		}
	}

	componentWillUnmount() {
		const { group, onUnload } = this.props;
		onUnload(group.id);
	}
	private renderHistoricViewDateInput(historicProductDataViewDate: DateValue) {
		const yearAgo = new Date();
		yearAgo.setDate(yearAgo.getDate() - 365 * 10);

		return (
			<label className={styles.parameterLabel} htmlFor='reference-historic-date'>
				<span className={styles.parameterLabelText}>Reference date:</span>
				<div className={styles.dateParameterInput}>
					<Debounce value={historicProductDataViewDate.value} onChange={this.historicViewDateChanged}>
						{({ value, onChange }) => (
							<DatePickerInput
								clearable
								dateValue={historicProductDataViewDate.dateValue}
								disabledDays={{ after: new Date(), before: yearAgo }}
								isInvalid={historicProductDataViewDate.isValid === false}
								placeholder='Today'
								styles={defaultInputStyles}
								value={value}
								onChange={onChange}
							/>
						)}
					</Debounce>
				</div>
			</label>
		);
	}

	private readonly historicViewDateChanged = (value: string) => {
		const dateValue = parseNumericHumanDate(value);
		this.setState(
			{
				state: {
					...this.resolveState(),
					historicProductDataViewDate: { value, dateValue, isValid: null },
				},
			},
			() => {
				this.update({ historicProductDataViewDate: dateValue });
			}
		);
	};

	renderVersionSelect(referenceBasketVersion: string) {
		const { czReimbursement, versions } = this.props;
		const currentVersion = versions && versions.find((version) => version.id === referenceBasketVersion);

		const descriptionIcon = (
			<span className={styles.parameterIcon}>
				<Icon name='violet-question-mark' />
			</span>
		);

		return (
			<label className={styles.parameterLabel} htmlFor='reference-basket-version'>
				<span className={styles.parameterLabelText}>Reference basket valid:</span>
				<div className={styles.parameterSelect}>
					<Select
						disabled={versions === null || czReimbursement === null}
						id='reference-basket-version'
						styles={{ select: styles.select }}
						value={referenceBasketVersion}
						onChange={this.referenceBasketVersionChanged}
					>
						{versions !== null && czReimbursement !== null
							? versions.map((version) => (
									<option key={version.id} value={version.id}>
										Since {formatNumericHumanDate(version.sinceDate)}
									</option>
							  ))
							: null}
					</Select>

					{currentVersion ? <Tooltip reference={descriptionIcon}>{currentVersion.description}</Tooltip> : null}
				</div>
			</label>
		);
	}

	referenceBasketVersionChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const referenceBasketVersion = event.target.value;
		this.setState(
			{
				state: {
					...this.resolveState(),
					referenceBasketVersion,
				},
			},
			() => {
				this.update({ referenceBasketVersion });
			}
		);
	};

	private renderPossibleSavingsRelativeToInput(possibleSavingsRelativeTo: string[]) {
		const { group } = this.props;

		return (
			<label className={styles.parameterLabel}>
				<span className={styles.parameterLabelText}>Possible savings groups:</span>
				<div className={styles.parameterAutocomplete}>
					<Debounce value={possibleSavingsRelativeTo} onChange={this.possibleSavingsRealtiveToChanged}>
						{({ value, onChange }) => (
							<SavingsGroupsAutocomplete groupId={group.id} selectedPossibleSavings={value} onChange={onChange} />
						)}
					</Debounce>
				</div>
			</label>
		);
	}

	private readonly possibleSavingsRealtiveToChanged = (possibleSavingsRelativeTo: string[]) => {
		this.setState(
			{
				state: {
					...this.resolveState(),
					possibleSavingsRelativeTo,
				},
			},
			() => {
				this.update({ possibleSavingsRelativeTo });
			}
		);
	};

	private resolveState(): SettingsState {
		const { czReimbursement } = this.props;
		const historicProductDataViewDate = czReimbursement?.reference.historicProductDataViewDate ?? null;

		return !this.state.state
			? {
					historicProductDataViewDate: {
						value: historicProductDataViewDate !== null ? formatNumericHumanDate(historicProductDataViewDate) : '',
						dateValue: historicProductDataViewDate,
						isValid: true,
					},
					referenceBasketVersion: czReimbursement?.reference.referenceBasketVersion ?? '',
					possibleSavingsRelativeTo: czReimbursement?.reference.possibleSavingsRelativeTo ?? [],
			  }
			: this.state.state;
	}

	private update(reference: Partial<GroupUpdateReference>) {
		const { group, onChange } = this.props;
		const state = this.resolveState();

		onChange(group.id, {
			referenceBasketVersion: state.referenceBasketVersion,
			historicProductDataViewDate: state.historicProductDataViewDate.dateValue,
			possibleSavingsRelativeTo: state.possibleSavingsRelativeTo,
			...reference,
		});
	}

	render() {
		const { czReimbursement, updateStatus } = this.props;
		const state = this.resolveState();

		if (czReimbursement === null) {
			return null;
		}

		return (
			<div className={styles.settingsHeader}>
				<div className={styles.in}>
					<div className={styles.parameters}>
						<p className={styles.parametersDescription}>Reimbursement calculation parameters:</p>

						{this.renderHistoricViewDateInput(state.historicProductDataViewDate)}
						{this.renderVersionSelect(state.referenceBasketVersion)}
						{this.renderPossibleSavingsRelativeToInput(state.possibleSavingsRelativeTo)}
						<SaveActivityIndicator hideAfterMilliseconds={3000} status={updateStatus} />
					</div>
				</div>
			</div>
		);
	}
}
